<template>
  <div>
    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ loadingError }}
        </p>
      </div>
    </b-alert>
    <b-row>
      <b-col
        align="right"
      >
        <b-button
          variant="outline-primary"
          class="mb-1 ml-2"
          @click="openAddMrForm"
        >
          Add MR
        </b-button>
      </b-col>
    </b-row>

    <b-table
      v-if="!loading && !loadingError"
      :items="processedResults"
      :fields="tableFields"
      responsive="sm"
      striped
      hover
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      class="compact-table"
      no-local-sorting
      @sort-changed="sortingChanged"
    >
      <template #cell(is_active)="{ item }">
        <app-chip :variant="item.is_active ? 'success' : 'secondary'">
          {{ item.is_active ? 'Active' : 'Inactive' }}
        </app-chip>
      </template>
      <template #cell(action)="data">
        <feather-icon
          v-b-tooltip.hover="{boundary:'window'}"
          title="Edit MR"
          icon="EditIcon"
          size="20"
          class="cursor-pointer mr-1"
          @click="openEditMrForm(data.item.id)"
        />
        <feather-icon
          v-b-tooltip.hover="{boundary:'window'}"
          title="View Details"
          icon="EyeIcon"
          size="20"
          class="cursor-pointer mr-1"
          @click="selectedCheckDetailId = data.item.id"
        />
      </template>
    </b-table>
    <detailed-pagination
      v-if="!loading && !loadingError"
      :total-items="totalItems"
      :per-page="perPage"
      :current-page="currentPage"
      @page-changed="(page) => {currentPage = page}"
    />
    <add-mr
      v-if="showMrForm"
      :id="selectedMrId"
      @modal-closed="closeMrForm"
      @mr-added="handleMrAdded"
      @mr-updated="handleMrUpdated"
    />
    <mr-details-model
      v-if="selectedCheckDetailId"
      :id="selectedCheckDetailId"
      @modal-closed="selectedCheckDetailId = null"
    />
  </div>
</template>

<script>
import {
  BSpinner, BTable, BAlert, BRow, BCol, BButton, VBTooltip,
} from 'bootstrap-vue'
import axios from 'axios'

import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import getParsedQueryString from '@/utils/headerQueryParser'
import AppChip from '@/components/UI/AppChip.vue'
import AddMr from './AddMr.vue'
import DetailedPagination from '../../UI/DetailedPagination.vue'
import MrDetailsModel from './MrDetails.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BSpinner,
    BTable,
    BRow,
    BCol,
    BButton,
    BAlert,
    AppChip,
    DetailedPagination,
    AddMr,
    MrDetailsModel,
  },
  mixins: [dateMixin, formatMixin],
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      loadingError: null,

      results: [],
      tableFields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'name', label: 'MR Name', sortable: true },
        { key: 'company_name', label: 'Company', sortable: true },
        { key: 'post', label: 'Post', sortable: true },
        { key: 'mobile_info', label: 'Mobile (Primary)', sortable: false },
        { key: 'whatsapp_info', label: 'Mobile (WhatsApp)', sortable: false },
        { key: 'email_info', label: 'Primary Email', sortable: false },
        { key: 'is_active', label: 'Status', sortable: true },
        { key: 'action', label: 'Action', sortable: false },
      ],
      selectedCheckDetailId: false,
      selectedMrId: null,
      currentPage: 1,
      perPage: 50,
      totalItems: 0,
      sortBy: 'created_at',
      sortDesc: true,
      submitting: false,
      showMrForm: false,
    }
  },
  computed: {
    processedResults() {
      return this.results.map(item => {
        // Find primary mobile or first mobile
        const primaryMobile = item.mr_mobiles?.find(mobile => mobile.is_primary)
        const whatsappMobile = item.mr_mobiles?.find(mobile => mobile.is_whatsapp)
        // Find primary email or first email
        const primaryEmail = item.mr_emails?.find(email => email.is_primary) || item.mr_emails?.[0] || null
        return {
          ...item,
          mobile_info: primaryMobile?.mobile,
          whatsapp_info: whatsappMobile?.mobile,
          email_info: primaryEmail?.email,
        }
      })
    },
  },
  watch: {
    currentPage() {
      this.fetchItems()
    },
    filters() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        this.fetchItems()
      }
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.fetchItems()
    },

    fetchItems() {
      const params = {
        sort_by: this.sortBy,
        sort_desc: this.sortDesc,
        page: this.currentPage,
        per_page: this.perPage,
        ...this.filters,
      }
      this.loading = true
      axios.get(`/mr/?${getParsedQueryString(params)}`)
        .then(res => {
          this.results = res.data.results
          this.totalItems = res.data.count
          this.loadingError = null
          this.loading = false
        }).catch(error => {
          this.loadingError = error?.response?.data?.error || 'Something went wrong'
          this.loading = false
        })
    },

    openAddMrForm() {
      this.selectedMrId = null
      this.showMrForm = true
    },

    openEditMrForm(mrId) {
      this.selectedMrId = mrId
      this.showMrForm = true
    },

    closeMrForm() {
      this.showMrForm = false
      this.selectedMrId = null
    },

    handleMrAdded() {
      this.closeMrForm()
      this.currentPage = 1
      this.fetchItems()
    },

    handleMrUpdated() {
      this.closeMrForm()
      this.fetchItems()
    },
  },
}
</script>

<style scoped>
.text-success {
  font-size: 1.2rem;
  font-weight: bold;
}
</style>
